import { useState, useEffect, createContext, useContext } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Toaster, toast } from '@/components/ui/sonner';
import ErrorBoundary from '@/components/ErrorBoundary';
import NotificationPrompt from '@/components/NotificationPrompt';
import PushRecoveryBanner from '@/components/PushRecoveryBanner';
import IOSInstallPopup from '@/components/IOSInstallPopup';
import ProfileCompletionPopup from '@/components/ProfileCompletionPopup';
import BiometricEnrollPrompt from '@/components/BiometricEnrollPrompt';
import BottomTabBar from '@/components/BottomTabBar';
import OpenInAppBanner from '@/components/OpenInAppBanner';
import SwipeBackGesture from '@/components/SwipeBackGesture';
import PausedAccountBanner from '@/components/PausedAccountBanner';
import { useIsIOSApp } from '@/hooks/useIsIOSApp';
import usePushNotifications from '@/hooks/usePushNotifications';
import { persistAuthToken, clearAuthToken } from '@/lib/authStorage';
import { subscribeUserToFcm, unsubscribeUserFromFcm } from '@/lib/iosFcmBridge';
import Landing from '@/pages/Landing';
import EventDiscovery from '@/pages/EventDiscovery';
import EventDetail from '@/pages/EventDetail';
import CreateEvent from '@/pages/CreateEvent';
import EditEvent from '@/pages/EditEvent';
import Dashboard from '@/pages/Dashboard';
import UserProfile from '@/pages/UserProfile';
import EnhancedProfile from '@/pages/EnhancedProfile';
import PrivacySettings from '@/pages/PrivacySettings';
import Membership from '@/pages/Membership';
import PaymentSuccess from '@/pages/PaymentSuccess';
import FindExpats from '@/pages/FindExpats';
import AdminDashboard from '@/pages/AdminDashboard';
import Groups from '@/pages/Groups';
import GroupDetail from '@/pages/GroupDetail';
import GroupInvite from '@/pages/GroupInvite';
import GroupInviteLink from '@/pages/GroupInviteLink';
import CityPage from '@/pages/CityPage';
import Messages from '@/pages/Messages';
import Articles from '@/pages/Articles';
import ProfileViewers from '@/pages/ProfileViewers';
import About from '@/pages/About';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import Marketing from '@/pages/Marketing';
import Terms from '@/pages/Terms';
import Contact from '@/pages/Contact';
import Support from '@/pages/Support';
import FAQ from '@/pages/FAQ';
import Jobs from '@/pages/Jobs';
import JobDetail from '@/pages/JobDetail';
import CreateJob from '@/pages/CreateJob';
import Businesses from '@/pages/Businesses';
import BusinessDetail from '@/pages/BusinessDetail';
import RegisterBusiness from '@/pages/RegisterBusiness';
import HousingPage from '@/pages/HousingPage';
import PropertyDetailPage from '@/pages/PropertyDetailPage';
import CreatePropertyPage from '@/pages/CreatePropertyPage';
import MySubmissionsPage from '@/pages/MySubmissionsPage';
import SavedItemsPage from '@/pages/SavedItemsPage';
import MarketplacePage from '@/pages/MarketplacePage';
import ListingDetailPage from '@/pages/ListingDetailPage';
import CreateListingPage from '@/pages/CreateListingPage';
import ConnectionDiagnostics from '@/pages/ConnectionDiagnostics';
import PushDiagnostics from '@/pages/PushDiagnostics';
import MyConnections from '@/pages/MyConnections';
import PollsPage from '@/pages/PollsPage';
import PollDetailPage from '@/pages/PollDetailPage';
import ResetPassword from '@/pages/ResetPassword';
import VerifyEmail from '@/pages/VerifyEmail';
import VerifyEmailChange from '@/pages/VerifyEmailChange';
import VerifyPending from '@/pages/VerifyPending';
import Onboarding from '@/pages/Onboarding';
import Notifications from '@/pages/Notifications';
import PWAInstallPrompt from '@/components/PWAInstallPrompt';

// Use relative URL so API calls always go to the same domain the app is served from.
// This works in both preview (ingress routes /api to backend) and production (same setup).
// Avoids cross-origin issues when preview URLs get baked into production builds.
const API = '/api';

// Auth Context
const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Helper to check if membership is active
export const isMembershipExpired = (user) => {
  if (!user) return false;
  if (!user.is_member) return true;
  if (user.membership_expires) {
    return new Date(user.membership_expires) < new Date();
  }
  return false;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // Mirror the token to IndexedDB on every app load so the SW always has it,
      // even for users who logged in before this change shipped.
      persistAuthToken(token);
      fetchUser();
    } else {
      setLoading(false);
    }

    // Global interceptor for membership-expired 403 errors
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 403 && 
            error.response?.data?.detail?.toLowerCase().includes('membership')) {
          toast.error('Membership expired', {
            description: 'Your complimentary membership has expired. Renew to continue creating content.',
            duration: 8000,
            action: {
              label: 'Renew',
              onClick: () => { window.location.href = '/membership'; }
            }
          });
        }
        return Promise.reject(error);
      }
    );

    // In-flight GET dedupe + stale-while-revalidate cache for list and
    // event-detail URLs. The in-flight Map coalesces concurrent duplicates;
    // the SWR cache serves fresh (<60s) results instantly and revalidates
    // stale (<2min) results in the background. Only cache-safe URLs are
    // cached — notification counts and messages stay live.
    const inflight = new Map();
    const swrCache = new Map(); // key -> { response, expiresAt, staleAt, refreshing }
    const CACHE_FRESH_MS = 60 * 1000;
    const CACHE_STALE_MS = 2 * 60 * 1000;
    const cacheableUrl = (url) =>
      /\/api\/events(\?|$)/.test(url) ||
      /\/api\/events\/by-location/.test(url) ||
      /\/api\/events\/by-radius/.test(url) ||
      /\/api\/events\/[A-Za-z0-9-]+(\?|$)/.test(url);
    const invalidateEventCaches = (eventId) => {
      for (const key of Array.from(swrCache.keys())) {
        if (!eventId) {
          if (/\/api\/events(\?|$)|\/api\/events\/by-location|\/api\/events\/by-radius/.test(key)) {
            swrCache.delete(key);
          }
        } else if (key.includes(`/events/${eventId}`)) {
          swrCache.delete(key);
        }
      }
    };
    // Expose a tiny imperative hook for callers that mutate events.
    window.__invalidateEventCaches = invalidateEventCaches;

    const reqInterceptor = axios.interceptors.request.use((cfg) => {
      // Any non-GET to /events/* means data likely changed → bust the cache.
      try {
        const m = (cfg.method || 'get').toLowerCase();
        if (m !== 'get' && typeof cfg.url === 'string' && cfg.url.includes('/api/events')) {
          const match = cfg.url.match(/\/api\/events\/([A-Za-z0-9-]+)/);
          invalidateEventCaches(match ? match[1] : null);
        }
      } catch {}
      return cfg;
    });

    const originalGet = axios.get.bind(axios);
    axios.get = (url, config = {}) => {
      if (config && config._skipDedupe) return originalGet(url, config);
      try {
        const paramsPart = config?.params ? JSON.stringify(config.params) : '';
        const authPart = axios.defaults.headers.common['Authorization'] || '';
        const key = `get|${url}|${paramsPart}|${authPart}`;
        const now = Date.now();

        // SWR fast path for cacheable URLs — only applied when caller didn't opt out.
        if (!config._skipCache && cacheableUrl(url)) {
          const entry = swrCache.get(key);
          if (entry && entry.expiresAt > now) {
            // Fresh: hand back a Promise that resolves immediately.
            return Promise.resolve(entry.response);
          }
          if (entry && entry.staleAt > now && !entry.refreshing) {
            // Stale but usable: serve immediately, refresh in background.
            entry.refreshing = true;
            originalGet(url, config)
              .then((res) => {
                swrCache.set(key, {
                  response: res, expiresAt: Date.now() + CACHE_FRESH_MS,
                  staleAt: Date.now() + CACHE_STALE_MS, refreshing: false,
                });
              })
              .catch(() => { entry.refreshing = false; });
            return Promise.resolve(entry.response);
          }
        }

        // In-flight dedupe for everything else (and the cold-cache path above).
        if (inflight.has(key)) return inflight.get(key);
        const p = originalGet(url, config)
          .then((res) => {
            if (!config._skipCache && cacheableUrl(url)) {
              swrCache.set(key, {
                response: res, expiresAt: Date.now() + CACHE_FRESH_MS,
                staleAt: Date.now() + CACHE_STALE_MS, refreshing: false,
              });
            }
            return res;
          })
          .finally(() => inflight.delete(key));
        inflight.set(key, p);
        return p;
      } catch {
        return originalGet(url, config);
      }
    };

    return () => {
      axios.interceptors.response.eject(interceptor);
      axios.interceptors.request.eject(reqInterceptor);
      axios.get = originalGet;
      try { delete window.__invalidateEventCaches; } catch {}
    };
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/users/me`);
      setUser(response.data);
      // iOS-only: re-subscribe to the per-user FCM topic on every app load.
      // Idempotent on the native side — guarantees the device stays subscribed
      // even if the wrapper restarted or topic was lost.
      if (response.data?.user_id) subscribeUserToFcm(response.data.user_id);
      
      // Show reminder toast for unverified users
      // email_verified === false means explicitly unverified (new user)
      // email_verified === undefined/null means legacy user (no verification required)
      if (response.data.email_verified === false) {
        // Use sessionStorage to only show once per session
        // Don't show on verify-email pages (user is actively verifying)
        const isVerifyPage = window.location.pathname.startsWith('/verify-email');
        const reminderShown = sessionStorage.getItem('email_verification_reminder');
        if (!reminderShown && !isVerifyPage) {
          sessionStorage.setItem('email_verification_reminder', 'true');
          setTimeout(() => {
            toast.warning('Please verify your email', {
              description: 'Check your inbox for a verification link to complete your registration.',
              duration: 8000,
              action: {
                label: 'Resend',
                onClick: async () => {
                  try {
                    await axios.post(`${API}/auth/resend-verification`);
                    toast.success('Verification email sent!');
                  } catch (error) {
                    toast.error('Failed to resend verification email');
                  }
                }
              }
            });
          }, 5000); // Delay 5 seconds to let the page load first
        }
      }

      // Update app badge with unread notification count
      try {
        const badgeRes = await axios.get(`${API}/notifications/unread-count`);
        const unread = badgeRes.data?.unread_count || 0;
        if (navigator.setAppBadge && unread > 0) {
          navigator.setAppBadge(unread);
        } else if (navigator.clearAppBadge && unread === 0) {
          navigator.clearAppBadge();
        }
      } catch {}

      // iOS PWA: Show toast for expired membership users directing them to the website
      if (!response.data.is_member) {
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
          (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
        const isStandalone = window.navigator.standalone === true ||
          window.matchMedia('(display-mode: standalone)').matches;
        if (isIOS && isStandalone) {
          const dismissedAt = localStorage.getItem('ios_membership_toast_dismissed');
          const sevenDays = 7 * 24 * 60 * 60 * 1000;
          if (!dismissedAt || Date.now() - parseInt(dismissedAt) > sevenDays) {
            setTimeout(() => {
              toast.info('Upgrade your membership', {
                description: 'Visit expatssphere.com in your browser to unlock all premium features.',
                duration: 12000,
                action: {
                  label: 'Dismiss',
                  onClick: () => {
                    localStorage.setItem('ios_membership_toast_dismissed', Date.now().toString());
                  }
                }
              });
            }, 6000);
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = (newToken, userData) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    // Persist to IndexedDB so the Service Worker can read the token during
    // background events like `pushsubscriptionchange` (when the app is closed).
    persistAuthToken(newToken);
    // iOS-only: subscribe the native Swift wrapper to this user's FCM topic
    // so the backend can deliver pushes via Firebase (reliability fallback).
    // Safe no-op on all other platforms.
    if (userData?.user_id) subscribeUserToFcm(userData.user_id);
  };

  const logout = () => {
    // Snapshot the user id before clearing state so we can unsubscribe FCM cleanly.
    const prevUserId = user?.user_id;
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
    clearAuthToken();
    if (prevUserId) unsubscribeUserFromFcm(prevUserId);
  };

  const refreshUser = async () => {
    try {
      const response = await axios.get(`${API}/users/me`);
      setUser(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to refresh user:', error);
      return null;
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  // Email verification check:
  // - is_verified === false → new unverified user, must verify
  // - is_verified === true → verified user, allow access
  // - is_verified === undefined/null → legacy user (existed before verification was added), allow access
  const needsVerification = user.is_verified === false;
  
  if (needsVerification && location.pathname !== '/verify-pending') {
    return <Navigate to="/verify-pending" replace />;
  }

  return children;
};

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// Notification prompt wrapper to access user context
const NotificationPromptWrapper = () => {
  const { user } = useAuth();
  return <NotificationPrompt user={user} />;
};

// Global push subscription sync — runs on every page load for logged-in users
// Ensures push subscriptions stay synced to backend after deployments/SW updates
const PushSubscriptionSync = () => {
  const { user } = useAuth();
  const { isSubscribed } = usePushNotifications();
  
  // Always send auth token to service worker so it can make authenticated requests
  useEffect(() => {
    if (user && 'serviceWorker' in navigator) {
      const token = localStorage.getItem('token');
      if (token) {
        navigator.serviceWorker.ready.then(reg => {
          const sw = navigator.serviceWorker.controller || reg.active;
          if (sw) sw.postMessage({ type: 'SET_TOKEN', token });
        });
      }
    }
  }, [user]);
  
  return null;
};

function App() {
  const isIOSApp = useIsIOSApp();
  return (
    <AuthProvider>
      <ErrorBoundary>
      <div className="App pb-16 md:pb-0">
        <BrowserRouter>
          <ScrollToTop />
          <SwipeBackGesture />
          <PushSubscriptionSync />
          <PausedAccountBanner />
          <div id="app-content" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/events" element={<EventDiscovery />} />
            <Route path="/events/:eventId" element={<EventDetail />} />
            {!isIOSApp && <Route path="/membership" element={<Membership />} />}
            {!isIOSApp && <Route path="/payment-success" element={<PaymentSuccess />} />}
            <Route path="/find-expats" element={<FindExpats />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email/:token" element={<VerifyEmail />} />
            <Route path="/verify-email-change/:token" element={<VerifyEmailChange />} />
            <Route path="/verify-pending" element={<VerifyPending />} />
            <Route path="/onboarding" element={
                <ProtectedRoute>
                  <Onboarding />
                </ProtectedRoute>
              }
            />
            <Route
              path="/notifications"
              element={
                <ProtectedRoute>
                  <Notifications />
                </ProtectedRoute>
              }
            />
            <Route
              path="/create-event"
              element={
                <ProtectedRoute>
                  <CreateEvent />
                </ProtectedRoute>
              }
            />
            <Route
              path="/edit-event/:eventId"
              element={
                <ProtectedRoute>
                  <EditEvent />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route path="/profile" element={
                <ProtectedRoute>
                  <EnhancedProfile />
                </ProtectedRoute>
              }
            />
            <Route path="/profile/:userId" element={<EnhancedProfile />} />
            <Route path="/privacy-settings" element={
                <ProtectedRoute>
                  <PrivacySettings />
                </ProtectedRoute>
              }
            />
            <Route path="/admin" element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route path="/groups" element={<Groups />} />
            <Route path="/groups/invite/:inviteId" element={<GroupInvite />} />
            <Route path="/g/invite/:token" element={<GroupInviteLink />} />
            <Route path="/groups/:slug" element={<GroupDetail />} />
            <Route path="/community/:city" element={<CityPage />} />
            <Route path="/messages" element={
                <ProtectedRoute>
                  <Messages />
                </ProtectedRoute>
              }
            />
            <Route path="/messages/:recipientId" element={
                <ProtectedRoute>
                  <Messages />
                </ProtectedRoute>
              }
            />
            <Route path="/guides" element={<Articles />} />
            <Route path="/guides/:slug" element={<Articles />} />
            <Route path="/profile-viewers" element={
                <ProtectedRoute>
                  <ProfileViewers />
                </ProtectedRoute>
              }
            />
            {/* Static Pages */}
            <Route path="/about" element={<About />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/marketing" element={<Marketing />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/support" element={<Support />} />
            <Route path="/faq" element={<FAQ />} />
            {/* Job Board */}
            <Route path="/jobs" element={<Jobs />} />
            <Route path="/jobs/create" element={
                <ProtectedRoute>
                  <CreateJob />
                </ProtectedRoute>
              }
            />
            <Route path="/jobs/:jobId" element={<JobDetail />} />
            <Route path="/jobs/:jobId/edit" element={
                <ProtectedRoute>
                  <CreateJob />
                </ProtectedRoute>
              }
            />
            {/* Business Directory */}
            <Route path="/businesses" element={<Businesses />} />
            <Route path="/businesses/register" element={
                <ProtectedRoute>
                  <RegisterBusiness />
                </ProtectedRoute>
              }
            />
            <Route path="/businesses/:businessId" element={<BusinessDetail />} />
            <Route path="/businesses/:businessId/edit" element={
                <ProtectedRoute>
                  <RegisterBusiness />
                </ProtectedRoute>
              }
            />
            
            {/* Housing Routes */}
            <Route path="/housing" element={<HousingPage />} />
            <Route path="/housing/create" element={
              <ProtectedRoute>
                <CreatePropertyPage />
              </ProtectedRoute>
            } />
            <Route path="/housing/:propertyId" element={<PropertyDetailPage />} />
            <Route path="/housing/:propertyId/edit" element={
              <ProtectedRoute>
                <CreatePropertyPage />
              </ProtectedRoute>
            } />
            
            {/* Marketplace Routes */}
            <Route path="/marketplace" element={<MarketplacePage />} />
            <Route path="/marketplace/create" element={
              <ProtectedRoute>
                <CreateListingPage />
              </ProtectedRoute>
            } />
            <Route path="/marketplace/:listingId" element={<ListingDetailPage />} />
            <Route path="/marketplace/:listingId/edit" element={
              <ProtectedRoute>
                <CreateListingPage />
              </ProtectedRoute>
            } />
            
            {/* My Submissions */}
            <Route path="/my-submissions" element={
              <ProtectedRoute>
                <MySubmissionsPage />
              </ProtectedRoute>
            } />
            
            {/* Saved Items */}
            <Route path="/saved" element={
              <ProtectedRoute>
                <SavedItemsPage />
              </ProtectedRoute>
            } />
            
            {/* Connection Diagnostics */}
            <Route path="/connection-diagnostics" element={
              <ProtectedRoute>
                <ConnectionDiagnostics />
              </ProtectedRoute>
            } />

            {/* My Connections — standalone access point */}
            <Route path="/connections" element={
              <ProtectedRoute>
                <MyConnections />
              </ProtectedRoute>
            } />

            {/* Push Diagnostics (admin only, gated inside page) */}
            <Route path="/admin/push-diagnostics" element={
              <ProtectedRoute>
                <PushDiagnostics />
              </ProtectedRoute>
            } />

            {/* Polls */}
            <Route path="/polls" element={<PollsPage />} />
            <Route path="/polls/:pollId" element={
              <ProtectedRoute>
                <PollDetailPage />
              </ProtectedRoute>
            } />
          </Routes>
          </div>
          <BottomTabBar />
        </BrowserRouter>
        <Toaster />
        <OpenInAppBanner />
        <PWAInstallPrompt />
        <NotificationPromptWrapper />
        <PushRecoveryBanner />
        <IOSInstallPopup />
        <ProfileCompletionPopup />
        <BiometricEnrollPrompt />
      </div>
      </ErrorBoundary>
    </AuthProvider>
  );
}

export default App;
export { API };